import { queryOptions, useQuery } from '@tanstack/react-query';

import { TimeRange, ApiResponse, QueryOptions } from '@cast/shared-types';
import { makeIsEnabled } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

const VALUE_REPORT_TIMELINE_QUERY_KEY = 'value-report-timeline';

const getValueReportTimelineQueryKey = ({
  clusterIds,
  key,
  step,
  useListingPrices,
  managedOnly,
  timeZone,
  range,
  organizationId,
}: {
  clusterIds: string[];
  key: string;
  step: 'ONE_DAY' | 'ONE_MONTH';
  useListingPrices?: boolean;
  managedOnly?: boolean;
  timeZone: string;
  range: TimeRange;
  organizationId: string | undefined;
}) =>
  [
    VALUE_REPORT_TIMELINE_QUERY_KEY,
    organizationId,
    clusterIds,
    key,
    step,
    useListingPrices,
    managedOnly,
    timeZone,
    range,
  ] as const;

const apiFn = apiClient.costReport.getValueReportTimeline;

type UseValueReportTimelineQueryData = ApiResponse<typeof apiFn>;

type UseValueReportTimelineQueryArgs<SELECT> = QueryOptions<
  UseValueReportTimelineQueryData,
  SELECT
> & {
  step?: 'ONE_DAY' | 'ONE_MONTH';
  timeZone: string;
  clusterIds: string[];
  key: string;
  getRange: () => TimeRange;
  useListingPrices?: boolean;
  managedOnly?: boolean;
};

const getValueReportTimelineQueryOptions = <
  SELECT = UseValueReportTimelineQueryData
>(
  {
    clusterIds,
    key,
    getRange,
    timeZone,
    step = 'ONE_DAY',
    useListingPrices,
    managedOnly,
    enabled = true,
    ...rest
  }: UseValueReportTimelineQueryArgs<SELECT>,
  organizationId: string | undefined
) => {
  const range = getRange();
  return queryOptions({
    queryKey: getValueReportTimelineQueryKey({
      clusterIds,
      key,
      step,
      useListingPrices,
      managedOnly,
      timeZone,
      range,
      organizationId,
    }),
    queryFn: async () => {
      const { data } = await apiFn(
        {
          id: organizationId ?? '',
          startTime: range.from,
          endTime: range.to,
          step,
          timeZone,
          useListingPrices,
          managedOnly,
          runValueRealizationTimelineReportRequestFilter: {
            clusterIds,
          },
        },
        { timeout: 120_000 }
      );
      return data;
    },
    enabled: makeIsEnabled(!!organizationId, enabled),
    ...rest,
  });
};

export const useValueReportTimelineQuery = <
  SELECT = UseValueReportTimelineQueryData
>(
  args: UseValueReportTimelineQueryArgs<SELECT>
) => {
  const { currentOrganization } = useOrganizations();

  return useQuery(
    getValueReportTimelineQueryOptions(args, currentOrganization?.id)
  );
};
